import FormGroupWithTooltip from '@/components/common/FormGroup/FormGroupWithTooltip';
import Select from '@/components/common/Select/Select';
import { SelectOption } from '@/hooks/useCatalogData';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrackEditViewTemplateProps } from '../TrackEditViewTemplate';
import { BaseFormGroupProps } from './types';

type Props = Pick<
  TrackEditViewTemplateProps,
  | 'handleInput'
  | 'baseMixedTrack'
  | 'trackData'
  | 'fieldHasErrors'
  | 'fieldValidationState'
  | 'isMultipleTrack'
  | 'findErrorMessage'
  | 'areNewMultipleTracks'
> &
  BaseFormGroupProps & {
    trackKey: string;
    isLoadingCatalogs: boolean;
    audioLanguages: SelectOption[];
    queryAudioLanguages: (inputText: string | null, callback: any) => void;
  };

export default function TrackAudioLanguageFormGroup({
  trackKey,
  isLoadingCatalogs,
  audioLanguages,
  queryAudioLanguages,
  trackData,
  baseMixedTrack,
  readonly,
  disabled,
  areNewMultipleTracks,
  isMultipleTrack,
  fieldHasErrors,
  fieldValidationState,
  findErrorMessage,
  handleInput,
}: Props) {
  const { t } = useTranslation();
  const isMixed =
    baseMixedTrack?.audioLanguage?.isMixed &&
    baseMixedTrack?.audioLanguage?.value === 'Mixed';

  return (
    <FormGroupWithTooltip
      tooltipKey="tooltip.track.audioLanguage"
      hasErrors={
        !isMultipleTrack
          ? fieldHasErrors('AudioLanguage') && !trackData.audioLanguage
          : false
      }
    >
      {audioLanguages.length > 0 && (
        <Select
          key={`audioLanguage-${trackKey}-${isLoadingCatalogs ? 'loading' : 'ready'}`}
          id="audioLanguageTypeahead"
          labelTitle={t('track.audioLanguage')}
          labelClass="col-sm-3 form-label"
          isClearable={!readonly}
          loadOptions={queryAudioLanguages}
          placeholder={isMixed ? t('track.mixed') : ''}
          onChange={(option) =>
            handleInput('audioLanguage', option?.value ?? null)
          }
          initialOption={
            isMixed
              ? null
              : audioLanguages.find((ml: any) =>
                  !isMultipleTrack
                    ? ml.value === trackData.audioLanguage
                    : ml.value === baseMixedTrack?.audioLanguage?.value
                )
          }
          className={isMixed ? 'col-sm-6 mixed-audio-language' : 'col-sm-6'}
          isReadOnly={readonly}
          isDisabled={disabled}
          isLoading={isLoadingCatalogs}
          hasErrors={
            !isMultipleTrack
              ? fieldHasErrors('AudioLanguage') && !trackData.audioLanguage
              : false
          }
          validationState={
            !isMultipleTrack
              ? fieldValidationState?.('AudioLanguage')
              : undefined
          }
          errorMessage={
            !isMultipleTrack ? findErrorMessage('AudioLanguage') : ''
          }
          baseOptions={audioLanguages ?? []}
          multitrack={baseMixedTrack?.audioLanguage}
          showChangeIndicator={!areNewMultipleTracks}
          handleUndo={(value) => handleInput('audioLanguage', value)}
        />
      )}
    </FormGroupWithTooltip>
  );
}
